<template>
  <div>
    <Loader :loaded="ordersLoaded || !orders.data" />

    <EmptyState v-if="(ordersLoaded && !orders.data.length) || !orders.data">
      You do not have any order history.
    </EmptyState>

    <div v-else class="background-color-white order-history__orders-wrapper">
      <tracking-popup v-if="selectedOrderNumber !== 0" />

      <v-container
        v-for="(order, i) in orders.data"
        :key="i"
        class="border-bottom rounded-bottom-lg"
        fluid
      >
        <v-row align="baseline" class="rounded-top-lg" :id="order.order_id">
          <v-col cols="5" md="2" class="order-history__order-placed">
            <div class="font-weight-semibold">Order Placed</div>
            {{ formatOrderPlacedDate(order.created_at) }}
          </v-col>
          <v-col
            cols="2"
            xs="2"
            md="1"
            class="d-flex flex-column align-center align-md-start"
          >
            <div class="font-weight-semibold">Items</div>
            {{ order.products.length }}
          </v-col>
          <v-col
            cols="2"
            xs="2"
            md="1"
            class="d-flex flex-column align-center align-md-start"
          >
            <div class="font-weight-semibold">Total</div>
            {{ formatCurrency(order.totals.total) }}
          </v-col>
          <v-col
            cols="2"
            xs="2"
            md="1"
            class="d-flex flex-column align-center align-md-start"
          >
            <div class="font-weight-semibold text-no-wrap">Order #</div>
            {{ order.order_id }}
          </v-col>

          <v-col cols="5" md="2" class="mt-sm-0 mt-md-3">
            <div class="font-weight-semibold">Status</div>

            <span v-if="order.delivery_method_id === 2">
              <span v-if="order.status === 17">
                Delivery at {{ order.myteamOrderDetails.pickupTime }}
              </span>
              <span v-if="order.status === 5">
                {{ formatOrderStatus(order.status).orderStatus }}
              </span>
              <span v-if="order.status === 7"> Order Canceled </span>
            </span>

            <span v-else-if="order.delivery_method_id === 3">
              {{
                order.status === 7
                  ? "Order Canceled"
                  : formatBringgStatus(
                      order.bringgOrderDetails.delivery_status,
                      true,
                    ).status
              }}
            </span>

            <div v-else-if="!order.delivery_method_id">
              <span v-if="order.status === 17">
                Pickup at: {{ order.pickupOrder.pickupTime }}
              </span>
              <span v-if="order.status === 7">Order Canceled</span>
              <span v-else>Pickup completed</span>
            </div>
          </v-col>

          <v-col cols="2" md="2" align-self="center">
            <div
              class="d-flex justify-center"
              v-if="showGreenCheck(order)"
            >
              <order-confirmation-check
                :orderStatus="'Delivery Completed'"
                :selectedOrderNumber="selectedOrderNumber"
              />
            </div>

            <div
              class="d-flex justify-center"
              v-else-if="showDeliveryIcon(order)"
            >
              <status-delivering :orderStatusCode="17" />
            </div>

            <div
              class="d-flex justify-center"
              v-else-if="showPickupIcon(order)"
            >
              <store-icon />
            </div>

            <div
              class="d-flex justify-center"
              v-else-if="order.status === 7"
            >
              <img src="@/assets/order_status/cancelIcon.svg" />
            </div>

            <BringgTrackingButton :orderId="order.order_id" v-else />
          </v-col>

          <v-col cols="12" md="2" class="">
            <a
              v-if="!showDetails(order.order_id)"
              class="cursor-pointer font-size-md-res font-weight-semibold text-decoration-underline"
              @click="scrollToOrderDetails(order.order_id)"
            >
              View Order Details
            </a>
            <a
              v-else
              class="cursor-pointer font-size-md-res font-weight-semibold text-decoration-underline"
              @click="removeFromOpenOrderDetails(order.order_id)"
            >
              Hide Order Details
            </a>
          </v-col>
        </v-row>

        <v-row class="background-color-white rounded-bottom-lg">
          <v-col cols="11" class="pa-0">
            <v-sheet :width="fixedRowWidth">
              <swiper class="swiper" ref="swipes" :options="swiperOptions">
                <swiper-slide
                  class="pt-3 d-flex justify-center"
                  v-for="(product, i) in order._products"
                  :key="i"
                >
                  <Product
                    :product="{
                      ...product,
                      is_redemption: false,
                      redemption_points_offer_id: null,
                    }"
                    :size="$vuetify.breakpoint.xsOnly ? 'xs' : 'md'"
                  />
                </swiper-slide>
                <div
                  v-if="$vuetify.breakpoint.smAndUp"
                  class="swiper-button-next"
                  slot="button-next"
                  @click="next(i)"
                ></div>
                <div
                  v-if="$vuetify.breakpoint.smAndUp"
                  class="swiper-button-prev"
                  slot="button-prev"
                  @click="prev(i)"
                ></div>
              </swiper>
            </v-sheet>
          </v-col>
        </v-row>

        <v-row :id="`details-${order.order_id}`">
          <v-col cols="12" lg="9" xl="9" class="pb-0 pb-sm-3">
            <transition name="fade">
              <v-col v-if="showDetails(order.order_id)">
                <v-row>
                  <v-col cols="12">
                    <div class="font-size-lg-res">
                      Order # {{ order.order_id }}
                    </div>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col>
                    <OrderDetails
                      :deliveryAmount="order.delivery_amount || 0"
                      :firstOrderDiscount="order.first_order_discount"
                      :products="order.products"
                      :totals="order.totals"
                      :updates="!!order.isPicked?.order.length"
                    />
                  </v-col>
                </v-row>
                <v-row
                  v-if="orderWithAddress(order, 'full_address')"
                  no-gutters
                >
                  <v-col cols="12">
                    <div
                      class="color-suva-grey font-size-lg-res font-weight-semibold"
                    >
                      Delivery Address
                    </div>
                    <div>{{ order.delivery_address.full_address }}</div>
                  </v-col>
                </v-row>
                <v-row v-if="orderWithAddress(order, 'apartment')" no-gutters>
                  <v-col cols="12">
                    <div
                      class="color-suva-grey font-size-lg-res font-weight-semibold"
                    >
                      Apt/Suite/Floor
                    </div>
                    <div>{{ order.delivery_address.apartment }}</div>
                  </v-col>
                </v-row>
              </v-col>
            </transition>
          </v-col>
        </v-row>
      </v-container>
    </div>
  </div>
</template>

<script>
import moment from "moment-timezone";
import { mapGetters, mapState } from "vuex";
import { formatCurrency } from "@/util.js";
import EmptyState from "@/components/EmptyState.vue";
import Loader from "@/components/UI/Loader.vue";
import OrderDetails from "@/components/Order/Details.vue";
import Product from "@/components/Product/index.vue";
import { formatBringgStatus } from "@/helper/orderHelperMethods";
import OrderConfirmationCheck from "@/assets/order_status/orderConfirmationCheck.vue";
import TrackingPopup from "@/components/Account/OrderHistory/TrackingPopup.vue";
import BringgTrackingButton from "@/components/Account/OrderHistory/BringgTrackingButton.vue";
import StatusDelivering from "@/assets/order_status/delivering.vue";
import { formatOrderStatus } from "@/helper/orderHelperMethods";
import StoreIcon from "@/assets/order_status/storeIcon.vue";

let scrolledToOrderIdOnLoad = false;

export default {
  name: "OrderHistory",
  components: {
    StoreIcon,
    StatusDelivering,
    BringgTrackingButton,
    TrackingPopup,
    OrderConfirmationCheck,
    EmptyState,
    Loader,
    OrderDetails,
    Product,
  },
  computed: {
    ...mapGetters("orders", {
      ordersLoaded: "loaded",
    }),
    ...mapState({
      orders: (state) => state.orders,
      selectedOrderNumber: (state) => state.orderStatus.selectedOrderNumber,
    }),
    fixedRowWidth() {
      return this.$vuetify.breakpoint.xsOnly
        ? `${this.innerWidth}px`
        : `${this.innerWidth - 292}px`;
    },
  },
  data() {
    return {
      openOrderDetailsList: [],
      innerWidth: window.innerWidth,
      swiperOptions: {
        slidesPerView: this.$vuetify.breakpoint.xsOnly ? 2.25 : 3,
        navigation: {
          nextEl: ".swiper-button-next",
          prevEl: ".swiper-button-prev",
        },
      },
    };
  },
  methods: {
    formatBringgStatus,
    formatOrderStatus,
    moment,

    showGreenCheck(order) {
      // Bringg orders
      if (order.delivery_method_id === 3) {
        return order.bringgOrderDetails.delivery_status === "task_checked_in";
      }
      // MyTeam Orders
      if (order.delivery_method_id === 2) {
        return order.status === 5;
      }
      if (!order.delivery_method_id) {
        return order.status === 5;
      }
      return false;
    },

    showDeliveryIcon(order) {
      if (order.delivery_method_id !== 2) return false;
      return order.status >= 17 || order.status <= 19;
    },

    showPickupIcon(order) {
      if (order.delivery_method_id) return false;
      return order.status !== 5 && order.status !== 7;
    },

    next(i) {
      this.$refs.swipes[i].$swiper.slideNext();
    },
    orderWithAddress(order, prop) {
      return order.delivery_method_id &&
        order.delivery_address &&
        order.delivery_address[prop]
        ? order.delivery_address[prop]
        : null;
    },
    prev(i) {
      this.$refs.swipes[i].$swiper.slidePrev();
    },
    formatCurrency,
    formatOrderPlacedDate(orderCreatedDate) {
      const orderPlacedDate = moment(orderCreatedDate);
      return this.$vuetify.breakpoint.lgAndUp
        ? orderPlacedDate.format("MMM D, YYYY, h:mm A")
        : orderPlacedDate.format("MMM D, YYYY, h:mm A");
    },

    getOrderIdFromEmailLink() {
      const { rerouteOrderId: orderIdFromLogin } = this.orders;
      const { orderId: orderIdFromUrl } = this.$route.params;

      orderIdFromLogin &&
        this.$store.commit("orders/ROUTE_TO_ORDER_HISTORY", "");

      return orderIdFromLogin || orderIdFromUrl
        ? orderIdFromLogin
          ? orderIdFromLogin
          : orderIdFromUrl
        : false;
    },

    removeFromOpenOrderDetails(id) {
      this.openOrderDetailsList = this.openOrderDetailsList.filter(
        (orderId) => orderId !== id,
      );
    },
    scrollToOrderDetails(id) {
      this.openOrderDetailsList = [...this.openOrderDetailsList, id];

      this.$nextTick(() => {
        const el = document.getElementById(`details-${id}`);
        // smAndUp: 4 + 76 + 76 = 156
        // xsOnly: = -194
        // 156 happens to work for both
        const yOffsetIsHeightOfFixedHeader = -156;
        const y =
          el.getBoundingClientRect().top +
          window.pageYOffset +
          yOffsetIsHeightOfFixedHeader;
        window.scrollTo({ top: y, behavior: "smooth" });
      });
    },
    scrollToOrderIdOnceOnLoad() {
      // run once on load...not for every update
      if (scrolledToOrderIdOnLoad) {
        return;
      }
      const orderId = this.getOrderIdFromEmailLink();
      if (!orderId) {
        return;
      }
      this.scrollToTopOfOrder(orderId);
    },
    scrollToTopOfOrder(orderId) {
      const el = document.getElementById(orderId);
      el && el.scrollIntoView({ behavior: "smooth", block: "center" });
      scrolledToOrderIdOnLoad = true;
    },
    showDetails(id) {
      return this.openOrderDetailsList.some((orderId) => orderId === id);
    },
    setInnerWidth() {
      this.innerWidth = window.innerWidth;
    },
  },
  mounted() {
    window.addEventListener("resize", this.setInnerWidth);
  },
  updated() {
    this.scrollToOrderIdOnceOnLoad();
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.setInnerWidth);
  },
};
</script>

<style lang="scss" scoped>
.order-history {
  &__orders-wrapper {
    padding-left: 11px;
  }

  &__order-placed {
    padding-right: 0;
  }
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.fade-enter-active,
.fade-leave-active {
  transition: all 1s ease-in-out;
}

/*swiper's opinionated default css needs some overrides*/
.rounded-bottom-lg {
  border-radius: 0 0 8px 8px;
}

.rounded-top-lg {
  border-radius: 8px 8px 0 0;
}

.swiper-button-prev.swiper-button-disabled,
.swiper-button-next.swiper-button-disabled {
  pointer-events: auto;
}

.swiper-button-next {
  color: var(--v-primary-base);
  /*pointer-events: none;*/
  right: 0;
  z-index: 0;
}

.swiper-button-next:after {
  font-size: 20px;
}

.swiper-button-prev {
  color: var(--v-primary-base);
  left: 0;
  z-index: 0;
}

.swiper-button-prev:after {
  font-size: 20px;
}
</style>
